import type Array from '@ember/array';
import type NativeArray from '@ember/array/-private/native-array';
import type MutableArray from '@ember/array/mutable';
import { action, get } from '@ember/object';
import { compare } from '@ember/utils';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import type { TableField } from 'garaje/pods/roomba/settings/calendars/controller';

export const MODELS_PER_PAGE = 25;

const SECONDARY_SORT_FIELD = 'displayName';

/**
 * Compares two field values, treating blanks as greater than any value so that they sort
 * last ascending and first descending — matching how Postgres orders NULLs, and therefore
 * how the server orders the sibling table on the rooms assignment page.
 */
function compareValues(a: unknown, b: unknown): number {
  const aIsBlank = a === null || a === undefined || a === '';
  const bIsBlank = b === null || b === undefined || b === '';

  if (aIsBlank || bIsBlank) {
    if (aIsBlank && bIsBlank) return 0;
    return aIsBlank ? 1 : -1;
  }

  return compare(a, b);
}

type RecordSelectTableComponentArgs<T> = {
  fields: TableField[];
  models: Array<T>;
  selectedModels: MutableArray<T>;
  recordCount: number;
  setPageNumber: (pageNumber: number) => void;
  pageNumber: number;
  handleSort: (sortField: string, sortDirection: 'asc' | 'desc') => void;
  isSortingEnabled: boolean;
};

export default class RecordSelectTableComponent<T> extends Component<RecordSelectTableComponentArgs<T>> {
  @tracked sortDirection = 'asc';
  @tracked sortField: string;
  @tracked pageNumber = 1;
  @tracked pageSize = MODELS_PER_PAGE;

  constructor(owner: RecordSelectTableComponent<T>, args: RecordSelectTableComponentArgs<T>) {
    super(owner, args);
    this.sortField = this.args.fields[0]?.name || '';
  }

  get sortedModels(): T[] {
    const { models, isSortingEnabled } = this.args;
    // if sorting is explicitly disabled, return the models as-is
    if (isSortingEnabled !== undefined && isSortingEnabled !== null && !isSortingEnabled) {
      return models as NativeArray<T>;
    }
    // Mirrors the server's `ORDER BY <field> <direction>, COALESCE(display_name, name) ASC`:
    // the secondary sort stays ascending in both directions, so the two tables on the rooms
    // assignment page break ties the same way.
    const direction = this.sortDirection === 'desc' ? -1 : 1;

    // Spread rather than toArray()/sortBy(): those are deprecated Array prototype
    // extensions. Copying also keeps sort() from mutating the caller's list.
    return [...(models as unknown as T[])].sort((a, b) => {
      const primary = compareValues(get(a as object, this.sortField), get(b as object, this.sortField));
      if (primary !== 0) {
        return primary * direction;
      }
      return compareValues(get(a as object, SECONDARY_SORT_FIELD), get(b as object, SECONDARY_SORT_FIELD));
    });
  }

  get paginatedModels(): T[] {
    const pageNumber = this.args.pageNumber ?? 1;
    return this.sortedModels.slice(pageNumber * this.pageSize - this.pageSize, pageNumber * this.pageSize);
  }

  get recordCount(): number {
    return this.args.recordCount ?? this.args.models.length;
  }

  @action
  setPageNumber(pageNumber: number): void {
    const { setPageNumber } = this.args;
    if (setPageNumber) {
      setPageNumber(pageNumber);
    }
  }

  @action
  handleSelectAll(): void {
    this.args.selectedModels.addObjects(this.args.models);
  }

  @action
  handleDeselectAll(): void {
    this.args.selectedModels.clear();
  }

  @action
  handleSelect(model: T): void {
    this.args.selectedModels.addObject(model);
  }

  @action
  handleDeselect(model: T): void {
    this.args.selectedModels.removeObject(model);
  }

  @action
  handleSort(sortField: string, sortDirection: 'asc' | 'desc'): void {
    this.sortField = sortField;
    this.sortDirection = sortDirection;

    if (this.args.handleSort) {
      this.args.handleSort(sortField, sortDirection);
    }

    this.setPageNumber(1);
  }
}
